import { useEffect } from "react";
import "@/index.css";
import { BrowserRouter, Routes, Route, useLocation, Outlet } from "react-router-dom";
import { MotionConfig } from "framer-motion";
import Lenis from "lenis";
import { Toaster } from "@/components/ui/sonner";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import { lenisRef } from "@/lib/lenis";
import Home from "@/pages/Home";
import { VenturesPage, VentureDetail } from "@/pages/Ventures";
import { ServicesPage, ServiceDetail } from "@/pages/Services";
import { HowWeBuild, EngagementModels } from "@/pages/Process";
import VenturePartnerships from "@/pages/VenturePartnerships";
import { About, Founder } from "@/pages/About";
import ContactPage from "@/pages/Contact";
import { Work, Insights, FaqPage, Careers } from "@/pages/Misc";
import { Privacy, Terms, Cookies } from "@/pages/Legal";
import NotFound from "@/pages/NotFound";
import { AdminLogin, AdminInbox } from "@/pages/Admin";

function SmoothScroll() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;
    const lenis = new Lenis({ duration: 1.15, easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)), smoothWheel: true });
    lenisRef.current = lenis;
    let raf = requestAnimationFrame(function loop(time) { lenis.raf(time); raf = requestAnimationFrame(loop); });
    return () => { cancelAnimationFrame(raf); lenis.destroy(); lenisRef.current = null; };
  }, []);
  useEffect(() => {
    if (hash) {
      const t = setTimeout(() => {
        const el = document.querySelector(hash);
        if (el) lenisRef.current ? lenisRef.current.scrollTo(el, { offset: -90 }) : el.scrollIntoView();
      }, 120);
      return () => clearTimeout(t);
    }
    lenisRef.current ? lenisRef.current.scrollTo(0, { immediate: true }) : window.scrollTo(0, 0);
    return undefined;
  }, [pathname, hash]);
  return null;
}

function SiteLayout() {
  return (
    <div className="grain relative min-h-screen">
      <a href="#main" className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[100] focus:rounded-lg focus:bg-si-cyan focus:px-4 focus:py-2 focus:text-si-bg">Skip to content</a>
      <Header />
      <main id="main"><Outlet /></main>
      <Footer />
    </div>
  );
}

export default function App() {
  return (
    <MotionConfig reducedMotion="user">
      <BrowserRouter>
        <SmoothScroll />
        <Routes>
          <Route element={<SiteLayout />}>
            <Route path="/" element={<Home />} />
            <Route path="/ventures" element={<VenturesPage />} />
            <Route path="/ventures/:slug" element={<VentureDetail />} />
            <Route path="/services" element={<ServicesPage />} />
            <Route path="/services/:slug" element={<ServiceDetail />} />
            <Route path="/how-we-build" element={<HowWeBuild />} />
            <Route path="/engagement-models" element={<EngagementModels />} />
            <Route path="/venture-partnerships" element={<VenturePartnerships />} />
            <Route path="/about" element={<About />} />
            <Route path="/about/founder" element={<Founder />} />
            <Route path="/work" element={<Work />} />
            <Route path="/insights" element={<Insights />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/faq" element={<FaqPage />} />
            <Route path="/careers" element={<Careers />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/cookies" element={<Cookies />} />
            <Route path="*" element={<NotFound />} />
          </Route>
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin" element={<AdminInbox />} />
        </Routes>
        <Toaster theme="dark" position="bottom-right" />
      </BrowserRouter>
    </MotionConfig>
  );
}
